import React, { useCallback, useEffect, useRef, useState } from "react";
import { loadSave, storeSave } from "./gameUtils";
import "./TapsiBubbles.css";

const API = "https://a9.asurahosting.com/api/nowplaying/tapsi_web_radio";
const SAVE_KEY = "tapsi-bubbles-v1";
const W = 360;
const H = 420;
const RANKS = ["Περαστικός", "Ακροατής", "Τακτικός", "Θαμώνας", "Συλλέκτης", "Δισκοπώλης", "DJ της γειτονιάς", "Θρύλος του Tapsi"];

const levelOf = (xp) => Math.floor(Math.sqrt(xp / 150)) + 1;
const xpFor = (lvl) => (lvl - 1) * (lvl - 1) * 150;
const rankOf = (lvl) => RANKS[Math.min(lvl - 1, RANKS.length - 1)];

const hash = (str) => {
  let h = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

// Every song gets its own palette (and bubble mood) derived from its id
const paletteFor = (id) => {
  const base = hash(id || "tapsi") % 360;
  return [0, 55, 110, 170, 230].map((o) => `hsl(${(base + o) % 360} 80% 60%)`);
};

export default function TapsiBubbles() {
  const [save, setSave] = useState(() => ({ xp: 0, pops: 0, bestCombo: 0, ...(loadSave(SAVE_KEY) || {}) }));
  const [song, setSong] = useState(null);

  const canvasRef = useRef(null);
  const saveRef = useRef(save);
  saveRef.current = save;
  const game = useRef({
    bubbles: [],
    parts: [],
    palette: paletteFor(""),
    combo: 0,
    comboColor: -1,
    comboT: 0,
    spawnIn: 0,
    label: "",
    labelT: 0,
    pendingXp: 0,
    pendingPops: 0,
    songChanged: 0,
  });

  useEffect(() => {
    storeSave(SAVE_KEY, save);
  }, [save]);

  const poll = useCallback(async () => {
    try {
      const data = await (await fetch(API)).json();
      const np = data.now_playing;
      if (!np || !np.song) return;
      const next = { id: np.song.id, title: np.song.title || np.song.text, artist: np.song.artist || "", art: np.song.art || "" };
      setSong((cur) => {
        if (cur && cur.id === next.id) return cur;
        game.current.palette = paletteFor(next.id);
        game.current.songChanged = 1;
        // Recolor the bubbles already on screen to the new song palette
        game.current.bubbles.forEach((b) => {
          b.c = Math.floor(Math.random() * 5);
        });
        return next;
      });
    } catch (e) {
      /* keep the last known song */
    }
  }, []);

  useEffect(() => {
    poll();
    const t = setInterval(poll, 10000);
    return () => clearInterval(t);
  }, [poll]);

  // Flush earned XP to the saved state a bit at a time so React is not re-rendering per pop
  useEffect(() => {
    const t = setInterval(() => {
      const g = game.current;
      if (!g.pendingPops) return;
      const xp = g.pendingXp;
      const pops = g.pendingPops;
      const combo = g.combo;
      g.pendingXp = 0;
      g.pendingPops = 0;
      setSave((s) => ({ ...s, xp: s.xp + xp, pops: s.pops + pops, bestCombo: Math.max(s.bestCombo, combo) }));
    }, 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const logo = new Image();
    logo.src = "/logotapsi.png";
    let raf;
    let last = performance.now();

    const tick = (now) => {
      const g = game.current;
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const lvl = levelOf(saveRef.current.xp + g.pendingXp);

      g.spawnIn -= dt;
      if (g.spawnIn <= 0) {
        const golden = lvl >= 2 && Math.random() < 0.06 + lvl * 0.005;
        const goldenDirection = Math.random() < 0.5 ? 1 : -1;
        const r = golden ? 32 : 20 + Math.random() * 22;
        g.bubbles.push({
          x: 30 + Math.random() * (W - 60),
          y: golden && goldenDirection === -1 ? -r : H + r,
          r,
          vy: golden ? 190 + Math.random() * 70 + lvl * 3 : 35 + Math.random() * 35 + lvl * 3,
          ph: Math.random() * 6.28,
          c: Math.floor(Math.random() * 5),
          golden,
          direction: golden ? goldenDirection : 1,
        });
        g.spawnIn = Math.max(0.25, 0.75 - lvl * 0.04);
      }
      g.bubbles.forEach((b) => {
        b.y -= b.vy * dt * (b.golden ? b.direction : 1);
        b.ph += dt * 1.5;
        b.x += Math.sin(b.ph) * 12 * dt;
        if (b.golden) {
          b.x = Math.max(b.r, Math.min(W - b.r, b.x));
        }
      });
      g.bubbles = g.bubbles.filter((b) => b.golden ? b.y < H + b.r && b.y > -b.r : b.y > -60);
      g.parts.forEach((p) => {
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vy += 220 * dt;
        p.life -= dt;
      });
      g.parts = g.parts.filter((p) => p.life > 0);
      g.comboT = Math.max(0, g.comboT - dt);
      if (g.comboT === 0) g.combo = 0;
      g.labelT = Math.max(0, g.labelT - dt);

      ctx.clearRect(0, 0, W, H);
      const grad = ctx.createLinearGradient(0, 0, 0, H);
      grad.addColorStop(0, "rgba(8,8,12,0.9)");
      grad.addColorStop(1, "rgba(30,12,20,0.9)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);

      g.bubbles.forEach((b) => {
        if (b.golden && logo.complete && logo.naturalWidth > 0) {
          ctx.drawImage(logo, b.x - b.r, b.y - b.r, b.r * 2, b.r * 2);
          return;
        }
        const col = b.golden ? "#ffd60a" : g.palette[b.c];
        const rg = ctx.createRadialGradient(b.x - b.r * 0.3, b.y - b.r * 0.3, b.r * 0.1, b.x, b.y, b.r);
        rg.addColorStop(0, "rgba(255,255,255,0.9)");
        rg.addColorStop(0.35, col);
        rg.addColorStop(1, col);
        ctx.globalAlpha = 0.9;
        ctx.fillStyle = rg;
        ctx.strokeStyle = "#000";
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.globalAlpha = 1;
      });
      g.parts.forEach((p) => {
        ctx.globalAlpha = Math.max(0, p.life * 2);
        ctx.fillStyle = p.col;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.s, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.globalAlpha = 1;

      ctx.textAlign = "center";
      ctx.font = "bold 18px 'Playpen Sans'";
      if (g.combo > 2) {
        ctx.fillStyle = "#ffb703";
        ctx.fillText(`${g.combo} στη σειρά`, W / 2, 28);
      }
      if (g.labelT > 0) {
        ctx.globalAlpha = Math.min(1, g.labelT);
        ctx.fillStyle = "#fff";
        ctx.fillText(g.label, W / 2, 60);
        ctx.globalAlpha = 1;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const pop = (e) => {
    const g = game.current;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * W;
    const y = ((e.clientY - rect.top) / rect.height) * H;
    let hit = -1;
    for (let i = g.bubbles.length - 1; i >= 0; i -= 1) {
      const b = g.bubbles[i];
      const isHit = b.golden
        ? Math.abs(b.x - x) <= b.r + 6 && Math.abs(b.y - y) <= b.r + 6
        : (b.x - x) ** 2 + (b.y - y) ** 2 <= (b.r + 6) ** 2;
      if (isHit) {
        hit = i;
        break;
      }
    }
    if (hit < 0) return;
    const [b] = g.bubbles.splice(hit, 1);
    const col = b.golden ? "#ffd60a" : g.palette[b.c];
    for (let i = 0; i < 12; i += 1) {
      const a = (i / 12) * Math.PI * 2;
      g.parts.push({ x: b.x, y: b.y, vx: Math.cos(a) * (60 + Math.random() * 80), vy: Math.sin(a) * (60 + Math.random() * 80) - 40, s: 2 + Math.random() * 3, life: 0.6, col });
    }
    // Popping the same color in a row builds a chain
    g.combo = !b.golden && b.c === g.comboColor ? g.combo + 1 : 1;
    g.comboColor = b.golden ? -1 : b.c;
    g.comboT = 2.5;
    const gain = b.golden ? 25 : 2 + Math.min(g.combo, 10);
    g.pendingXp += gain;
    g.pendingPops += 1;
    if (b.golden) {
      g.label = "ΤΑΨΙ! +25 XP";
      g.labelT = 1.5;
      for (let i = g.bubbles.length - 1; i >= 0; i -= 1) {
        if (Math.abs(g.bubbles[i].y - b.y) < 80) g.bubbles[i].c = b.c;
      }
    }
  };

  const lvl = levelOf(save.xp);
  const to = xpFor(lvl + 1);
  const pct = ((save.xp - xpFor(lvl)) / (to - xpFor(lvl))) * 100;

  return (
    <section className="tbb" aria-label="Σκάσε Φούσκες">
      <p className="tbb-sub">
        Σκάσε Φούσκες basically. Τα χρώματα αλλάζουν με το τραγούδι που παίζει. Σκάσε φούσκες ίδιου χρώματος στη σειρά και με τα ΤΑΨΙ πάρε +25XP!
      </p>

      <div className="tbb-level">
        <div className="tbb-level-top">
          <strong>Level {lvl}</strong>
          <span>{rankOf(lvl)}</span>
          <span>{save.xp}/{to} XP</span>
        </div>
        <div className="tbb-bar"><div className="tbb-bar-fill" style={{ width: `${pct}%` }} /></div>
      </div>

      {song && (
        <div className="tbb-song">
          {song.art && <img src={song.art} alt="" />}
          <span>
            <small>Παίζει τώρα</small>
            <strong>{song.title}</strong>
            {song.artist && <em>{song.artist}</em>}
          </span>
        </div>
      )}

      <canvas ref={canvasRef} width={W} height={H} className="tbb-canvas" onPointerDown={pop} />

      <p className="tbb-stats">
        Φούσκες: {save.pops} · Καλύτερο chain: {save.bestCombo}
      </p>
    </section>
  );
}
